import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Directive, Inject, Input, NgModule } from '@angular/core';
import { WINDOW } from '@upr-web-primer/window';
import { Subject, fromEvent } from 'rxjs';

class KeyboardA11yService {
    constructor() {
        this.$resetTrigger = new Subject();
        this.$keyTriggered = new Subject();
    }
}
KeyboardA11yService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
KeyboardA11yService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class KeyboardA11yDirective {
    constructor(document, window, keyboardService, renderer, _elRef, zone) {
        this.document = document;
        this.window = window;
        this.keyboardService = keyboardService;
        this.renderer = renderer;
        this._elRef = _elRef;
        this.zone = zone;
        this.activeNodeList = [];
        this.untouched = true;
        this.timeoutDelay = 100;
        this.autoFocus = true;
        this.documentContext = false;
        this.querySelect = [
            '.keyboard-a11y-capture',
            'button:not([tabindex="-1"]):not([disabled])',
            'a[href]:not([href=""]):not([tabindex="-1"])',
            '[type="checkbox"]:not([tabindex="-1"])',
            '[type="radio"]:not([tabindex="-1"])',
            '[role="checkbox"]:not([tabindex="-1"])',
            '[role="button"]:not([tabindex="-1"])',
            '[role="radio"]:not([tabindex="-1"])',
            '[role="link"]:not([tabindex="-1"])'
        ].join(',');
        this.keyCodes = {
            ArrowDown: 1,
            ArrowUp: -1,
            ArrowRight: 1,
            ArrowLeft: -1,
            Home: (elements) => {
                if (elements.length > 0) {
                    if (!!elements[0].getAttribute('aria-expanded') &&
                        elements.length > 1) {
                        elements[1].focus();
                        return;
                    }
                    elements[0].focus();
                }
            },
            End: (elements) => {
                if (elements.length > 1) {
                    elements[elements.length - 1].focus();
                }
            }
        };
    }
    ngOnInit() {
        this.$subscriptions = this.keyboardService.$resetTrigger.subscribe(() => this.ngAfterViewInit());
        this.$subscriptions.add(fromEvent(this.window, 'keyboard-a11y-reset').subscribe((event) => {
            const detail = event
                .detail;
            if (detail && detail.element) {
                const check = this._elRef.nativeElement.contains(detail.element);
                if (check) {
                    this.resetNodeList();
                    if (this.untouched) {
                        const index = this.activeNodeList.indexOf(detail.element);
                        if (index === 0) {
                            this.zone.runOutsideAngular(() => {
                                setTimeout(() => {
                                    detail.element.focus();
                                }, 100);
                            });
                        }
                    }
                }
            }
        }));
    }
    ngAfterViewInit() {
        if (this.enabled) {
            this.zone.runOutsideAngular(() => {
                setTimeout(() => {
                    this.resetNodeList();
                    this.timeoutDelay = 100;
                }, this.timeoutDelay);
            });
        }
    }
    ngOnChanges(changes) {
        const enabled = changes.enabled;
        if (enabled.currentValue === '') {
            this.enabled = true;
        }
        if (enabled.previousValue !== undefined) {
            if (enabled.previousValue === false && enabled.currentValue === true) {
                this.ngAfterViewInit();
            }
        }
    }
    setupEvent() {
        if (!this.mainKeyUpEvent) {
            if (this.activeNodeList.length && this.autoFocus) {
                this.activeNodeList[0].focus();
            }
            this.mainKeyUpEvent = this.renderer.listen(this.documentContext ? this.document : this._elRef.nativeElement, 'keyup', (event) => {
                if (event && this.enabled) {
                    event.stopImmediatePropagation();
                    if (Object.keys(this.keyCodes).includes(event.key)) {
                        this.untouched = false;
                        if ((typeof this.keyCodes[event.key]).toLowerCase() === 'number') {
                            const type = this.keyCodes[event.key];
                            const current = this.findFocusedElement();
                            if (current) {
                                const currentIndex = this.activeNodeList.indexOf(current);
                                if (currentIndex !== -1) {
                                    let location = 0;
                                    if (currentIndex + type < 0) {
                                        location = this.activeNodeList.length - 1;
                                    }
                                    else if (currentIndex + type <=
                                        this.activeNodeList.length - 1) {
                                        location = currentIndex + type;
                                    }
                                    this.activeNodeList[location].focus();
                                }
                            }
                        }
                        else {
                            this.keyCodes[event.key](this.activeNodeList);
                        }
                        this.keyboardService.$keyTriggered.next(event.key);
                    }
                }
            });
        }
    }
    findFocusedElement() {
        return this._elRef.nativeElement.querySelector(':focus');
    }
    resetNodeList() {
        this.activeNodeList = Array.from(this._elRef.nativeElement.querySelectorAll(this.querySelect));
        this.setupEvent();
    }
    ngOnDestroy() {
        if (this.$subscriptions && this.$subscriptions.unsubscribe) {
            this.$subscriptions.unsubscribe();
        }
        if (this.mainKeyUpEvent) {
            this.mainKeyUpEvent();
        }
    }
}
KeyboardA11yDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yDirective, deps: [{ token: DOCUMENT }, { token: WINDOW }, { token: KeyboardA11yService }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
KeyboardA11yDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: KeyboardA11yDirective, selector: "[uprKeyboardA11y]", inputs: { enabled: ["uprKeyboardA11y", "enabled"], timeoutDelay: "timeoutDelay", autoFocus: "autoFocus", documentContext: "documentContext", querySelect: "querySelect", keyCodes: "keyCodes" }, providers: [KeyboardA11yService], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[uprKeyboardA11y]',
                    providers: [KeyboardA11yService]
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: KeyboardA11yService }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.NgZone }]; }, propDecorators: { enabled: [{
                type: Input,
                args: ['uprKeyboardA11y']
            }], timeoutDelay: [{
                type: Input
            }], autoFocus: [{
                type: Input
            }], documentContext: [{
                type: Input
            }], querySelect: [{
                type: Input
            }], keyCodes: [{
                type: Input
            }] } });

class KeyboardA11yModule {
}
KeyboardA11yModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
KeyboardA11yModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yModule, declarations: [KeyboardA11yDirective], exports: [KeyboardA11yDirective] });
KeyboardA11yModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: KeyboardA11yModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [KeyboardA11yDirective],
                    imports: [],
                    exports: [KeyboardA11yDirective]
                }]
        }] });

export { KeyboardA11yDirective, KeyboardA11yModule, KeyboardA11yService };
